import { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes, extract } from '../lib/api';
import CookingMode from './CookingMode';
import { HighlightedText } from '../components/CookingTermPopover';
import TagEditor, { RecipeTagsDisplay } from '../components/TagEditor';
import RecipeThumbnail from '../components/RecipeThumbnail';

function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCookingMode, setShowCookingMode] = useState(false);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [scaledServings, setScaledServings] = useState(null);
  const [scaledIngredients, setScaledIngredients] = useState(null);

  // Inline editing states
  const [showIngredientEditor, setShowIngredientEditor] = useState(false);
  const [showNotesEditor, setShowNotesEditor] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [editedIngredients, setEditedIngredients] = useState([]);
  const [editedNotes, setEditedNotes] = useState([]);
  const [editedUserNotes, setEditedUserNotes] = useState('');
  const [showIngredientSaveWarning, setShowIngredientSaveWarning] = useState(false);
  const [showNotesSaveWarning, setShowNotesSaveWarning] = useState(false);
  const [includePhotoInPrint, setIncludePhotoInPrint] = useState(true);
  const printFrameRef = useRef(null);

  const { data: recipe, isLoading, error } = useQuery({
    queryKey: ['recipe', id],
    queryFn: () => recipes.get(id),
  });

  // Fetch more recipes from the same creator
  const { data: creatorRecipesData } = useQuery({
    queryKey: ['creatorRecipes', recipe?.sourceCreator, id],
    queryFn: () => recipes.getByCreator(recipe.sourceCreator, { excludeRecipeId: id, limit: 10 }),
    enabled: !!recipe?.sourceCreator,
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: () => recipes.toggleFavorite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => recipes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      navigate('/recipes');
    },
  });

  const scaleMutation = useMutation({
    mutationFn: (targetServings) => extract.scale(id, targetServings),
    onSuccess: (data) => {
      setScaledServings(data.targetServings);
      setScaledIngredients(data.scaledIngredients);
    },
  });

  const updateIngredientsMutation = useMutation({
    mutationFn: (ingredients) => recipes.update(id, { ingredients }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      setShowIngredientEditor(false);
      setScaledServings(null);
      setScaledIngredients(null);
    },
  });

  const updateNotesMutation = useMutation({
    mutationFn: ({ notes, userNotes }) => recipes.update(id, { notes, userNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      setShowNotesEditor(false);
    },
  });

  const openIngredientEditor = () => {
    setEditedIngredients(recipe.ingredients.map((ing, idx) => ({
      id: idx,
      amount: ing.amount || '',
      unit: ing.unit || '',
      item: ing.item || '',
      notes: ing.notes || '',
    })));
    setShowIngredientEditor(true);
  };

  const openNotesEditor = () => {
    setEditedNotes(recipe.notes || []);
    setEditedUserNotes(recipe.userNotes || '');
    setShowNotesEditor(true);
  };

  const handlePrint = () => {
    const printContent = generatePrintHTML();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    setShowPrintModal(false);
  };

  const generatePrintHTML = () => {
    const ingredientsList = recipe.ingredients
      .map(ing => `<li>${ing.amount || ''} ${ing.unit || ''} ${ing.item}${ing.notes ? ` (${ing.notes})` : ''}</li>`)
      .join('');

    const instructionsList = recipe.instructions
      .map((inst, idx) => `<li><strong>Step ${inst.step || idx + 1}:</strong> ${inst.text}</li>`)
      .join('');

    const notesList = recipe.notes?.length
      ? `<h3>Notes</h3><ul>${recipe.notes.map(n => `<li>${n}</li>`).join('')}</ul>`
      : '';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${recipe.title} - Recipe Grabber</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
          h1 { margin-bottom: 10px; }
          .meta { color: #666; margin-bottom: 20px; }
          .photo { max-width: 100%; height: auto; margin-bottom: 20px; border-radius: 8px; }
          h2, h3 { margin-top: 24px; border-bottom: 1px solid #eee; padding-bottom: 8px; }
          ul, ol { padding-left: 24px; }
          li { margin-bottom: 8px; line-height: 1.5; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${recipe.title}</h1>
        ${recipe.description ? `<p class="meta">${recipe.description}</p>` : ''}
        <p class="meta">
          ${recipe.prepTime ? `Prep: ${recipe.prepTime} | ` : ''}
          ${recipe.cookTime ? `Cook: ${recipe.cookTime} | ` : ''}
          ${recipe.servings ? `Servings: ${recipe.servings}` : ''}
        </p>
        ${includePhotoInPrint && recipe.thumbnailUrl ? `<img class="photo" src="${recipe.thumbnailUrl}" alt="" />` : ''}
        <h2>Ingredients</h2>
        <ul>${ingredientsList}</ul>
        <h2>Instructions</h2>
        <ol>${instructionsList}</ol>
        ${notesList}
        <p style="margin-top: 40px; color: #999; font-size: 12px;">Printed from Recipe Grabber</p>
      </body>
      </html>
    `;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Recipe not found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">This recipe may have been deleted.</p>
        <Link to="/recipes" className="btn btn-primary">
          Back to Recipes
        </Link>
      </div>
    );
  }

  const displayIngredients = scaledIngredients || recipe.ingredients;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Header */}
      <div className="card overflow-hidden mb-8">
        <div className="aspect-video bg-gray-100 dark:bg-gray-800">
          <RecipeThumbnail recipe={recipe} />
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{recipe.title}</h1>
              {recipe.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">{recipe.description}</p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowCookingMode(true)}
                className="btn btn-primary"
                title="Start Cooking"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Start Cooking
              </button>
              <button
                onClick={() => toggleFavoriteMutation.mutate()}
                className={`btn-icon ${recipe.isFavorite ? 'text-yellow-500' : 'text-gray-400'}`}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill={recipe.isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
              <Link to={`/recipe/${id}/edit`} className="btn-icon">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
              <button
                onClick={() => setShowPrintModal(true)}
                className="btn-icon"
                title="Print Recipe"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn-icon text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap gap-4 mt-6">
            {recipe.prepTime && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>Prep: {recipe.prepTime}</span>
              </div>
            )}
            {recipe.cookTime && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                </svg>
                <span>Cook: {recipe.cookTime}</span>
              </div>
            )}
            {recipe.servings && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>{scaledServings ? `${scaledServings} servings` : recipe.servings}</span>
              </div>
            )}
          </div>

          {/* Source */}
          {(recipe.sourceUrl || recipe.sourceCreator) && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {recipe.sourcePlatform || 'Source'}
                </span>
                {recipe.sourceCreator && (
                  <span className="text-gray-600 dark:text-gray-400">by {recipe.sourceCreator}</span>
                )}
                {recipe.sourceUrl && (
                  <a
                    href={recipe.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600 text-sm"
                  >
                    View original
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Confidence */}
          <div className="mt-4">
            <span className={`badge ${
              recipe.confidence === 'high' ? 'badge-success' :
              recipe.confidence === 'medium' ? 'badge-warning' :
              'badge-error'
            }`}>
              {recipe.confidence} confidence extraction
            </span>
          </div>

          {/* Tags */}
          <RecipeTagsDisplay recipe={recipe} onEditClick={() => setShowTagEditor(true)} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Ingredients */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ingredients</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={openIngredientEditor}
                className="text-sm text-primary-500 hover:text-primary-600"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  const newServings = prompt('Scale to how many servings?', '4');
                  if (newServings) {
                    scaleMutation.mutate(parseInt(newServings));
                  }
                }}
                className="text-sm text-primary-500 hover:text-primary-600"
                disabled={scaleMutation.isPending}
              >
                {scaleMutation.isPending ? 'Scaling...' : 'Scale'}
              </button>
            </div>
          </div>

          {scaledServings && (
            <div className="mb-4 p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg text-sm text-primary-600 dark:text-primary-400">
              Scaled to {scaledServings} servings
              <button
                onClick={() => {
                  setScaledServings(null);
                  setScaledIngredients(null);
                }}
                className="ml-2 underline"
              >
                Reset
              </button>
            </div>
          )}

          <ul className="space-y-3">
            {displayIngredients.map((ing, index) => (
              <li key={index} className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
                <span className="text-gray-700 dark:text-gray-300">
                  <strong className="text-primary-500">
                    {ing.amount} {ing.unit}
                  </strong>{' '}
                  {ing.item}
                  {ing.notes && <span className="text-gray-500 text-sm ml-1">({ing.notes})</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Instructions */}
        <div className="lg:col-span-2 card p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Instructions</h2>
          <ol className="space-y-4">
            {recipe.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center font-semibold">
                  {instruction.step || index + 1}
                </span>
                <p className="text-gray-700 dark:text-gray-300 pt-1">
                  <HighlightedText text={instruction.text} />
                </p>
              </li>
            ))}
          </ol>

          {/* Notes */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-md font-semibold text-gray-900 dark:text-white">Notes</h3>
              <button
                onClick={openNotesEditor}
                className="text-sm text-primary-500 hover:text-primary-600"
              >
                Edit
              </button>
            </div>
            {recipe.notes && recipe.notes.length > 0 && (
              <ul className="space-y-2 mb-4">
                {recipe.notes.map((note, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                    <svg className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                    </svg>
                    {note}
                  </li>
                ))}
              </ul>
            )}
            {recipe.userNotes && (
              <div className={recipe.notes?.length > 0 ? 'pt-4 border-t border-gray-100 dark:border-gray-700' : ''}>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Notes</h4>
                <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{recipe.userNotes}</p>
              </div>
            )}
            {(!recipe.notes || recipe.notes.length === 0) && !recipe.userNotes && (
              <p className="text-gray-500 dark:text-gray-400 italic">No notes yet. Click Edit to add some.</p>
            )}
          </div>
        </div>
      </div>

      {/* More from this creator */}
      {creatorRecipesData?.recipes?.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            More from {recipe.sourceCreator}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {creatorRecipesData.recipes.map((creatorRecipe) => (
              <Link
                key={creatorRecipe.id}
                to={`/recipe/${creatorRecipe.id}`}
                className="card overflow-hidden hover:ring-2 hover:ring-primary-500 transition-all"
              >
                <div className="aspect-video bg-gray-100 dark:bg-gray-800">
                  <RecipeThumbnail recipe={creatorRecipe} />
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2">
                    {creatorRecipe.title}
                  </h3>
                  {creatorRecipe.totalTime && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {creatorRecipe.totalTime}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Recipe?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{recipe.title}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn bg-red-500 text-white hover:bg-red-600"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cooking Mode */}
      {showCookingMode && (
        <CookingMode recipe={recipe} onClose={() => setShowCookingMode(false)} />
      )}

      {/* Tag Editor */}
      <TagEditor
        recipeId={id}
        isOpen={showTagEditor}
        onClose={() => setShowTagEditor(false)}
      />

      {/* Ingredient Editor Modal */}
      {showIngredientEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Edit Ingredients</h3>
            <div className="space-y-3">
              {editedIngredients.map((ing, index) => (
                <div key={ing.id} className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={ing.amount}
                    onChange={(e) => {
                      const updated = [...editedIngredients];
                      updated[index].amount = e.target.value;
                      setEditedIngredients(updated);
                    }}
                    placeholder="Amt"
                    className="input w-16"
                  />
                  <input
                    type="text"
                    value={ing.unit}
                    onChange={(e) => {
                      const updated = [...editedIngredients];
                      updated[index].unit = e.target.value;
                      setEditedIngredients(updated);
                    }}
                    placeholder="Unit"
                    className="input w-20"
                  />
                  <input
                    type="text"
                    value={ing.item}
                    onChange={(e) => {
                      const updated = [...editedIngredients];
                      updated[index].item = e.target.value;
                      setEditedIngredients(updated);
                    }}
                    placeholder="Ingredient"
                    className="input flex-1"
                  />
                  <input
                    type="text"
                    value={ing.notes}
                    onChange={(e) => {
                      const updated = [...editedIngredients];
                      updated[index].notes = e.target.value;
                      setEditedIngredients(updated);
                    }}
                    placeholder="Notes"
                    className="input w-32"
                  />
                  <button
                    onClick={() => {
                      setEditedIngredients(editedIngredients.filter((_, i) => i !== index));
                    }}
                    className="btn-icon text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                setEditedIngredients([...editedIngredients, {
                  id: Date.now(),
                  amount: '',
                  unit: '',
                  item: '',
                  notes: '',
                }]);
              }}
              className="btn btn-secondary mt-4"
            >
              + Add Ingredient
            </button>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowIngredientEditor(false)}
                className="btn btn-secondary"
                disabled={updateIngredientsMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => setShowIngredientSaveWarning(true)}
                disabled={updateIngredientsMutation.isPending}
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ingredient Save Warning Modal */}
      {showIngredientSaveWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="card p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Save Changes?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your ingredient edits will be saved permanently. This cannot be undone. Please double-check your changes before saving.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowIngredientSaveWarning(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowIngredientSaveWarning(false);
                  updateIngredientsMutation.mutate(editedIngredients.map(ing => ({
                    amount: ing.amount,
                    unit: ing.unit,
                    item: ing.item,
                    notes: ing.notes,
                  })));
                }}
                disabled={updateIngredientsMutation.isPending}
                className="btn btn-primary"
              >
                {updateIngredientsMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Editor Modal */}
      {showNotesEditor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Edit Notes</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Recipe Notes
                </label>
                <div className="space-y-2">
                  {editedNotes.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => {
                          const updated = [...editedNotes];
                          updated[index] = e.target.value;
                          setEditedNotes(updated);
                        }}
                        className="input flex-1"
                      />
                      <button
                        onClick={() => setEditedNotes(editedNotes.filter((_, i) => i !== index))}
                        className="btn-icon text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setEditedNotes([...editedNotes, ''])}
                  className="btn btn-secondary mt-2 text-sm"
                >
                  + Add Note
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Personal Notes
                </label>
                <textarea
                  value={editedUserNotes}
                  onChange={(e) => setEditedUserNotes(e.target.value)}
                  placeholder="Add your own notes, tips, or modifications..."
                  rows={4}
                  className="input"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowNotesEditor(false)}
                className="btn btn-secondary"
                disabled={updateNotesMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => setShowNotesSaveWarning(true)}
                disabled={updateNotesMutation.isPending}
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Save Warning Modal */}
      {showNotesSaveWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="card p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Save Changes?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your note changes will be saved permanently. This cannot be undone. Please double-check your changes before saving.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNotesSaveWarning(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowNotesSaveWarning(false);
                  updateNotesMutation.mutate({
                    notes: editedNotes.filter(n => n.trim()),
                    userNotes: editedUserNotes.trim() || null,
                  });
                }}
                disabled={updateNotesMutation.isPending}
                className="btn btn-primary"
              >
                {updateNotesMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Print Recipe</h3>

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePhotoInPrint}
                  onChange={(e) => setIncludePhotoInPrint(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-gray-700 dark:text-gray-300">Include recipe photo</span>
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-8">
                Photos may use more ink when printing.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPrintModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handlePrint}
                className="btn btn-primary"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecipeDetail;
